import classnames from 'classnames';
import omit from 'omit.js';
import React, { useState, useEffect, useCallback, useContext } from 'react';
import {getNid, WebUI} from "../../wui-core/src/index"
import {setComponentSize} from "../../wui-core/src/componentStyle"
import { RadioProps, OptionalProps, RadioValue, RadioGroupProps } from './iRadio'

const noop = () => {}

const defaultProps = {
    inverse: false,
    disabled: false,
    readOnly: false,
    browser: false,
    antd: false,
    size: 'default',
    color: '',
    onChange: noop,
    defaultChecked: false,
    style: {},
    value: '',
    fieldid: ''
};

/**
 * 创建Context用于与Radio.Group通信
 */
export const RadioGroupContext = React.createContext<{
    name?: string;
    selectedValue?: RadioValue;
    onChange?: RadioGroupProps['onChange'];
    size?: RadioGroupProps['size'];
    focusValue?: RadioValue;
} | null>(null);

/**
 * Radio 函数式组件
 */
const Radio: React.FC<RadioProps> = (props) => {
    // 从Context获取radioGroup信息
    const radioGroup = useContext(RadioGroupContext);

    // 解构props
    const {
        inverse = false,
        disabled = false,
        readOnly = false,
        browser = false,
        antd = false,
        color = '',
        className,
        children,
        clsPrefix,
        style = {},
        fieldid = '',
        checked: propsChecked,
        defaultChecked = false,
        onChange = noop,
        value = '',
        ...others
    } = props;

    // 初始化状态
    const initChecked = typeof propsChecked !== 'undefined' ? propsChecked : defaultChecked;
    const [checked, setChecked] = useState<boolean>(!!initChecked);
    const [focused, setFocused] = useState<boolean>(false);
    const [hovered, setHovered] = useState<boolean>(false);

    // 处理受控组件的checked状态变化 (对应getDerivedStateFromProps)
    useEffect(() => {
        if ('checked' in props && propsChecked !== checked) {
            setChecked(!!propsChecked);
        }
    }, [propsChecked]);

    // 点击处理函数
    const handleClick = useCallback((event: any) => {
        if (event?.target?.tagName !== "INPUT") {
            // 不可用的radio或者radio中的链接被点击时,不触发点击事件
            event.stopPropagation();
            return;
        }

        if (radioGroup && radioGroup.onChange) {
            // 在RadioGroup中
            let val = value as RadioValue;
            if (antd) {
                // 如果是antd用法的话,target返回与ant-design类似的属性结构的对象
                const targetDom = event.target;
                event.target = {
                    targetDom, // input对象
                    ...props
                };
                val = event;
            }
            radioGroup.onChange(val, event);
        } else {
            // 单独使用的Radio
            if (!('checked' in props)) {
                setChecked(!checked);
            }
            event.target.checked = !checked;
            onChange?.(event, !checked);
        }
    }, [radioGroup, antd, value, props, checked, onChange]);

    // 鼠标悬停处理
    const changeInputHovered = useCallback((event: React.MouseEvent<HTMLInputElement>, hovered: boolean) => {
        if (event.target && (event.target as HTMLInputElement).type === 'radio') {
            setHovered(hovered);
        }
    }, []);

    // 焦点处理
    const handleFocus = useCallback((event: React.FocusEvent<HTMLInputElement>, focused: boolean) => {
        if (event.target && event.target.type === 'radio') {
            setFocused(focused);
        }
    }, []);

    // 构建radioProps
    const radioProps: any = {...others};
    const isNoEventHandler = disabled || readOnly || browser; // 这两个状态的radio没有事件处理

    // 从radioGroup获取属性
    if (radioGroup) {
        radioProps.name = radioGroup.name;
        radioProps.selectedValue = radioGroup.selectedValue;
        radioProps.size = radioGroup.size;
        radioProps.focusValue = radioGroup.focusValue;
    }
    const {name, selectedValue, size, focusValue} = radioProps;

    let optional: OptionalProps = {};
    /**
     * 若父级selectedValue与本身的value值相同,则该radio被选中
     */
    if (selectedValue !== undefined) {
        optional.checked = (value === selectedValue);
    }

    // 构建CSS类
    let classes = {
        [`${clsPrefix}-read-only`]: readOnly,
        [`${clsPrefix}-browser`]: browser,
        [`${clsPrefix}-focused`]: focused,
        [`${clsPrefix}-hovered`]: hovered,
        'is-checked': typeof optional.checked !== 'undefined' ? optional.checked : checked,
        disabled
    };

    if (color) {
        classes[`${clsPrefix}-${color}`] = true;
    }
    if (size && setComponentSize(size)) {
        classes[`${clsPrefix}-${setComponentSize(size) === ('default' as any) ? 'sm' : setComponentSize(size)}`] = true;
    }
    if (inverse) {
        classes[`${clsPrefix}-inverse`] = true;
    }
    if (!children || (typeof children === 'string' && children.trim() === '')) {
        classes[`${clsPrefix}-no-content`] = true;
    }

    const classNames = classnames(clsPrefix, classes);
    let tabIndex = optional.checked ? 0 : -1;
    if (focusValue && focusValue == value) {
        tabIndex = 0;
    }

    // 事件处理器
    const mouseEnterHandler = isNoEventHandler ? noop : (event: React.MouseEvent<HTMLInputElement>) => changeInputHovered(event, true);
    const mouseLeaveHandler = isNoEventHandler ? noop : (event: React.MouseEvent<HTMLInputElement>) => changeInputHovered(event, false);
    const focusHandler = isNoEventHandler ? noop : (event: React.FocusEvent<HTMLInputElement>) => handleFocus(event, true);
    const blurHandler = isNoEventHandler ? noop : (event: React.FocusEvent<HTMLInputElement>) => handleFocus(event, false);

    const inputFieldidProp = fieldid ? { fieldid: `${fieldid}_radio` } : {};
    const readOnlyProp = readOnly || browser ? { readonly: "readonly" } : {};
    // 修复：单独使用 Radio 时，value 应来自 props 而非 radioProps
    const rawValue = (radioProps.value !== undefined) ? radioProps.value : value;
    const valueProp = typeof rawValue === 'boolean' ? String(rawValue) : rawValue; // input的value不接收bool值

    const input = (
        <input
            // @ts-ignore
            {...omit(radioProps, ["antd", "selectedValue", "checked", "value", "size", "onChange"])}
            type="radio"
            name={name}
            disabled={disabled}
            tabIndex={tabIndex}
            onFocus={focusHandler}
            onBlur={blurHandler}
            onMouseEnter={mouseEnterHandler}
            onMouseLeave={mouseLeaveHandler}
            {...inputFieldidProp}
            {...readOnlyProp}
            value={valueProp}
        />
    );

    const adapterNid = getNid(props); // 适配nid、uitype
    const fieldidProp = fieldid ? { fieldid } : {};

    return (
        // @ts-ignore
        <label
            style={style}
            onClick={isNoEventHandler ? noop : handleClick}
            {...fieldidProp}
            className={classnames(className, classNames)}
            {...adapterNid}
            {...omit(radioProps, ["antd", "selectedValue", "checked", "value", "size", "onChange", "name", "focusValue"])}
        >
            {input}
            <span className={clsPrefix + '-label'}>
                {children || <span className={clsPrefix + '-inner-no-content'}/>}
            </span>
        </label>
    );
};

export default WebUI({name: "radio", defaultProps})(Radio);
